(() => {
  'use strict';
  const configNode = document.getElementById('roscoff-cart-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const text = config.labels;
  const preopeningLocked = document.documentElement.dataset.roscoffPreopeningLocked !== 'false';
  const preopeningLabel = document.documentElement.dataset.roscoffPreopeningLabel;
  const root = window.Shopify?.routes?.root || `${config.root.replace(/\/$/, '')}/`;
  const catalog = window.ROSCOFF_COMMERCE;
  const mapped = new Map();
  for (const [reference, supports] of Object.entries(catalog?.shopifyVariantIds || {})) {
    for (const [support, formats] of Object.entries(supports)) {
      for (const [format, id] of Object.entries(formats)) mapped.set(String(id), {reference, support, format});
    }
  }
  for (const [reference, formats] of Object.entries(catalog?.shopifyAluKitVariants || {})) {
    for (const [format, variant] of Object.entries(formats)) mapped.set(String(variant.id), {reference, support: 'alu-dibond', format, withKit: true});
  }
  const purchase = document.querySelector('[data-roscoff-purchase]');
  const page = document.querySelector('[data-roscoff-cart-page]');
  const checkoutButton = page?.querySelector('[data-roscoff-checkout]');
  let cartItemsForValidity = [];
  const expiredPreorder = () => cartItemsForValidity.some(item => {
    const itemConfig = mapped.get(String(item.variant_id));
    if (itemConfig?.support !== 'encadre' || itemConfig.format !== 'F1') return false;
    const until = Date.parse(item.properties?.['_Roscoff acceptation avant']);
    return !Number.isFinite(until) || Date.now() > until;
  });
  let checkoutCartHasItems = checkoutButton?.dataset.cartHasItems === 'true';
  page?.querySelector('[data-roscoff-checkout-form]')?.addEventListener('submit', event => {
    if (busy || expiredPreorder() || !checkoutCartHasItems || checkoutButton?.dataset.checkoutEnabled !== 'true') event.preventDefault();
  });
  let sequence = Promise.resolve();
  let busy = false;
  let refreshing = 0;
  let restoreFocus = null;
  const selectionDisabled = new WeakMap();
  const money = (cents, currency = 'EUR') => new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency, minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: cents % 100 ? 2 : 0
  }).format(cents / 100);
  const variantQuantity = (cart, id) => cart.items.reduce((sum, item) =>
    sum + (String(item.variant_id) === String(id) ? item.quantity : 0), 0);
  function validateQuantity(value) {
    const number = Number(value);
    if (!Number.isInteger(number) || number < 1) throw new Error(text.invalid);
    if (number > 10) throw new Error(text.maximum);
    return number;
  }
  function assertLimit(cart, id, quantity, previous = 0) {
    if (variantQuantity(cart, id) - previous + quantity > 10) throw new Error(text.maximum);
  }
  async function request(endpoint, body) {
    if (preopeningLocked && endpoint === 'cart/add.js') throw new Error(preopeningLabel);
    try {
    const response = await fetch(root + endpoint, {
      credentials: 'same-origin', cache: 'no-store',
      ...(body ? {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)} : {headers: {Accept: 'application/json'}})
    });
    const result = await response.json();
    if (!response.ok) throw new Error(body && endpoint === 'cart/add.js' && [404, 422].includes(response.status) ? text.unavailable : text.failure);
    return result;
    } catch (error) {
      if ([text.unavailable, text.failure].includes(error.message)) throw error;
      throw new Error(text.failure);
    }
  }
  const getCart = () => request('cart.js');
  function stepState(control) {
    const input = control.querySelector('input');
    const value = Number(input.value);
    control.querySelector('[data-quantity-step="-1"]').disabled = busy || value <= 1;
    control.querySelector('[data-quantity-step="1"]').disabled = busy || value >= 10;
    input.disabled = busy;
  }
  function syncControls() {
    if (checkoutButton) {
      const enabled = checkoutButton.dataset.checkoutEnabled === 'true' && checkoutCartHasItems && !expiredPreorder() && !busy;
      checkoutButton.disabled = !enabled;
      checkoutButton.setAttribute('aria-disabled', String(!enabled));
    }
    document.querySelectorAll('[data-quantity-control]').forEach(stepState);
    document.querySelectorAll('[data-cart-add], [data-cart-remove]').forEach(button => {
      const addLocked = preopeningLocked && button.matches('[data-cart-add]');
      button.disabled = addLocked || busy || button.closest('[data-roscoff-purchase]')?.dataset.purchaseEnabled === 'false';
      if (addLocked) { button.textContent = preopeningLabel; button.setAttribute('aria-disabled', 'true'); }
      else button.setAttribute('aria-disabled', String(button.disabled));
    });
    if (purchase) {
      const panel = purchase.closest('[data-commercial-panel]');
      panel.setAttribute('aria-busy', String(busy));
      for (const button of panel.querySelectorAll('[data-support], [data-format], [data-alu-kit]')) {
        if (busy) {
          if (!selectionDisabled.has(button)) selectionDisabled.set(button, button.disabled);
          button.disabled = true;
        } else if (selectionDisabled.has(button)) {
          button.disabled = selectionDisabled.get(button);
          selectionDisabled.delete(button);
        }
      }
    }
    if (!busy && restoreFocus) { const restore = restoreFocus; restoreFocus = null; restore(); }
  }
  function feedback(message, form = purchase) {
    const output = form?.querySelector('[data-cart-feedback]') || page?.querySelector('[data-cart-page-feedback]');
    if (output) output.textContent = message;
  }
  const cartCopy = page ? JSON.parse(page.querySelector('[data-cart-copy]')?.textContent || '{}') : {};
  const originalImages = page ? JSON.parse(page.querySelector('[data-cart-original-images]')?.textContent || '{}') : {};
  function populateCartLine(line, item, configuration) {
    const values = (item.options_with_values || []).map(option => option.value);
    const rawSupport = (configuration?.support || values[0] || '').toLowerCase();
    const isAlu = rawSupport.includes('alu');
    const isFrame = rawSupport.includes('encadr');
    const isEdition = Boolean(item.properties?.['Édition']) || item.product_title === 'Évasion';
    line.querySelector('[data-cart-support]').textContent = isAlu ? cartCopy.alu_support : isFrame ? cartCopy.framed_support : isEdition ? cartCopy.fine_art_support : cartCopy.photo_support;
    line.querySelector('[data-cart-format]').textContent = values.slice(1).join(' · ');
    const characteristics = [];
    if (isEdition) characteristics.push(cartCopy.fine_art_paper, cartCopy.satin_finish);
    if (isAlu) characteristics.push(cartCopy.gloss_finish, cartCopy.hanging_kit);
    else if (isFrame) characteristics.push(cartCopy.black_frame, cartCopy.white_mount);
    else if (!isEdition) characteristics.push(cartCopy.standard_paper, cartCopy.satin_finish);
    const description = line.querySelector('[data-cart-characteristics]');
    for (const value of characteristics.filter(Boolean)) {
      const paragraph = document.createElement('p');
      paragraph.textContent = value;
      description.append(paragraph);
    }
    const properties = line.querySelector('[data-cart-properties]');
    for (const [key, value] of Object.entries(item.properties || {})) {
      if (key.startsWith('_') || ['Finition', 'Accroche'].includes(key) || value == null || String(value).trim() === '') continue;
      const paragraph = document.createElement('p');
      paragraph.textContent = `${key} : ${value}`;
      properties.append(paragraph);
    }
    line.querySelector('[data-cart-preorder]').hidden = !item.properties?.['Livraison différée'];
    for (const link of line.querySelectorAll('[data-cart-product-link]')) {
      link.href = item.url;
      if (link.classList.contains('roscoff-cart-media')) link.setAttribute('aria-label', item.product_title);
    }
    const source = originalImages[String(item.product_id)];
    if (source) {
      const image = document.createElement('img');
      image.className = 'roscoff-cart-image';
      image.alt = item.product_title;
      image.decoding = 'async';
      image.src = source;
      line.querySelector('.roscoff-cart-media').append(image);
    }
  }
  function render(cart) {
    cartItemsForValidity = cart.items;
    const count = cart.item_count;
    checkoutCartHasItems = count > 0;
    syncControls();
    for (const link of document.querySelectorAll('[data-roscoff-cart-link]')) {
      const badge = link.querySelector('[data-roscoff-cart-count]');
      badge.hidden = count === 0;
      badge.textContent = count > 99 ? '99+' : String(count);
      link.setAttribute('aria-label', count ? `${text.cart}, ${count} ${count === 1 ? text.article : text.articles}` : text.cart);
    }
    if (!page) return;
    page.querySelector('[data-cart-empty]').hidden = count > 0;
    page.querySelector('[data-cart-content]').hidden = count === 0;
    const items = page.querySelector('[data-cart-items]');
    const focused = document.activeElement;
    const focusedKey = focused?.closest('[data-cart-line]')?.dataset.key;
    const focusedAction = focused?.dataset.quantityStep || (focused?.matches('input') ? 'input' : focused?.matches('[data-cart-remove]') ? 'remove' : '');
    const fragment = document.createDocumentFragment();
    for (const item of cart.items) {
      const line = page.querySelector('[data-cart-line-template]').content.firstElementChild.cloneNode(true);
      line.dataset.key = item.key;
      line.querySelector('[data-cart-title]').textContent = item.product_title;
      const configuration = mapped.get(String(item.variant_id));
      populateCartLine(line, item, configuration);
      const priceDetail = line.querySelector('[data-cart-price-detail]');
      priceDetail.hidden = item.quantity < 2;
      priceDetail.textContent = item.quantity > 1 ? `${item.quantity} × ${money(item.final_price, cart.currency)}` : '';
      line.querySelector('[data-cart-line-total]').textContent = money(item.final_line_price, cart.currency);
      line.querySelector('input').value = item.quantity;
      fragment.append(line);
    }
    items.replaceChildren(fragment);
    page.querySelector('[data-cart-total]').textContent = money(cart.total_price, cart.currency);
    syncControls();
    if (focusedKey) {
      const line = [...items.children].find(row => row.dataset.key === focusedKey);
      const target = focusedAction === 'input' ? line?.querySelector('input') : focusedAction === 'remove' ? line?.querySelector('[data-cart-remove]') : line?.querySelector(`[data-quantity-step="${focusedAction}"]`);
      restoreFocus = () => {
        if (target && !target.disabled) target.focus();
        else { const fallback = items.querySelector('input') || page.querySelector('[data-cart-empty] a'); fallback?.focus(); }
      };
      if (!busy) { const restore = restoreFocus; restoreFocus = null; restore(); }
    }
  }
  function mutate(task, form) {
    sequence = sequence.catch(() => {}).then(async () => {
      busy = true; refreshing++; syncControls();
      const previousLabel = purchase?.querySelector('[data-cart-add]');
      if (previousLabel && form === purchase) previousLabel.textContent = text.adding;
      feedback(text.loading, form);
      try {
        // Web Locks also serializes cooperating Roscoff tabs in supporting browsers.
        const run = async () => { await task(); };
        if (navigator.locks) await navigator.locks.request('roscoff-cart-write', run);
        else await run();
      } catch (error) {
        feedback(error.message || text.failure, form);
        // A failed request is never represented as a successful local cart mutation.
        try { render(await getCart()); } catch (_) { /* Keep the last verified cart. */ }
      } finally {
        busy = false; syncControls();
        if (previousLabel) previousLabel.textContent = purchase.dataset.actionLabel || text.add;
      }
    });
    return sequence;
  }
  purchase?.addEventListener('roscoff:configuration', syncControls);
  purchase?.addEventListener('submit', event => {
    event.preventDefault();
    if (preopeningLocked) { feedback(preopeningLabel); return; }
    const id = purchase.dataset.variantId;
    let quantity;
    try { quantity = validateQuantity(purchase.querySelector('[name="quantity"]').value); }
    catch (error) { feedback(error.message); return; }
    if (!mapped.has(id) || purchase.hidden || purchase.dataset.purchaseEnabled === 'false' || (purchase.dataset.acceptUntil && Date.now() > Date.parse(purchase.dataset.acceptUntil))) { feedback(text.unavailable); return; }
    if (busy) return;
    purchase.querySelector('[data-cart-success-link]').hidden = true;
    mutate(async () => {
      if (preopeningLocked || purchase.dataset.purchaseEnabled === 'false' || purchase.dataset.variantId !== id || (purchase.dataset.acceptUntil && Date.now() > Date.parse(purchase.dataset.acceptUntil))) throw new Error(text.unavailable);
      const cart = await getCart();
      assertLimit(cart, id, quantity);
      const properties = Object.fromEntries([...new FormData(purchase)].filter(([name]) => name.startsWith('properties[')).map(([name, value]) => [name.slice(11, -1), value]));
      await request('cart/add.js', {items: [{id, quantity, properties}]});
      const updated = await getCart();
      render(updated);
      if (variantQuantity(updated, id) !== variantQuantity(cart, id) + quantity) throw new Error(text.failure);
      feedback(text.added);
      if (purchase.dataset.variantId === id) purchase.querySelector('[data-cart-success-link]').hidden = false;
    }, purchase);
  });
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-quantity-step], [data-cart-remove]');
    if (!button || busy) return;
    const line = button.closest('[data-cart-line]');
    if (line) {
      const key = line.dataset.key;
      const current = Number(line.querySelector('input').value);
      const quantity = button.matches('[data-cart-remove]') ? 0 : current + Number(button.dataset.quantityStep);
      changeLine(key, quantity); return;
    }
    if (!button.closest('[data-roscoff-purchase]')) return;
    const control = button.closest('[data-quantity-control]');
    const input = control.querySelector('input');
    const value = Number(input.value) + Number(button.dataset.quantityStep);
    try { input.value = validateQuantity(value); feedback(''); }
    catch (error) { feedback(error.message); }
    stepState(control);
  });
  document.addEventListener('input', event => {
    const input = event.target;
    if (!input.matches('[data-quantity-control] input') || busy || input.value === '') return;
    try { validateQuantity(input.value); feedback('', input.closest('[data-cart-line]') ? page : purchase); }
    catch (error) {
      feedback(error.message, input.closest('[data-cart-line]') ? page : purchase);
      input.value = String(Math.max(1, Math.min(10, Math.floor(Number(input.value) || 1))));
    }
    stepState(input.closest('[data-quantity-control]'));
  });
  document.addEventListener('change', event => {
    const input = event.target;
    if (!input.matches('[data-quantity-control] input') || busy) return;
    const line = input.closest('[data-cart-line]');
    try {
      const quantity = validateQuantity(input.value);
      if (line) changeLine(line.dataset.key, quantity);
      else { feedback(''); stepState(input.closest('[data-quantity-control]')); }
    } catch (error) {
      feedback(error.message, line ? page : purchase);
      if (line) refresh();
      else { input.value = Number(input.value) > 10 ? '10' : '1'; stepState(input.closest('[data-quantity-control]')); }
    }
  });
  function changeLine(key, quantity) {
    if (quantity !== 0) {
      try { validateQuantity(quantity); } catch (error) { feedback(error.message, page); return; }
    }
    mutate(async () => {
      const cart = await getCart();
      const line = cart.items.find(item => item.key === key);
      if (!line) throw new Error(text.failure);
      if (quantity) assertLimit(cart, line.variant_id, quantity, line.quantity);
      const updated = await request('cart/change.js', {id: key, quantity});
      render(updated);
      feedback(text.updated, page);
    }, page);
  }
  async function refresh() {
    const revision = ++refreshing;
    try { const cart = await getCart(); if (revision === refreshing && !busy) render(cart); }
    catch (_) { /* Do not replace server-rendered values with invented empty data. */ }
  }
  window.addEventListener('pageshow', refresh);
  window.addEventListener('focus', refresh);
  syncControls();
  refresh();
})();

(() => {
  const control = document.querySelector('.artwork-detail [data-roscoff-purchase] [data-quantity-control]');
  if (!control) return;
  const input = control.querySelector('input');
  const form = control.closest('[data-roscoff-purchase]');
  const coarse = window.matchMedia('(any-pointer: coarse)');
  const picker = document.createElement('select');
  picker.className = 'roscoff-quantity-picker';
  picker.setAttribute('aria-label', input.getAttribute('aria-label'));
  for (let value = 1; value <= 10; value++) {
    picker.add(new Option(String(value), String(value)));
  }
  control.append(picker);
  function syncPicker() {
    control.classList.toggle('is-touch-quantity', coarse.matches || navigator.maxTouchPoints > 0);
    picker.value = input.value;
    picker.disabled = input.disabled;
  }
  picker.addEventListener('change', () => {
    if (input.disabled) return;
    input.value = picker.value;
    input.dispatchEvent(new Event('input', {bubbles: true}));
    input.dispatchEvent(new Event('change', {bubbles: true}));
    syncPicker();
  });
  input.addEventListener('input', syncPicker);
  input.addEventListener('change', syncPicker);
  form.addEventListener('roscoff:configuration', () => queueMicrotask(syncPicker));
  coarse.addEventListener('change', syncPicker);
  new MutationObserver(syncPicker).observe(input, {attributes: true, attributeFilter: ['disabled']});
  syncPicker();
})();




